import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3, Users, Trophy, TrendingUp, PhoneCall, AlertTriangle,
  Percent, Download, Loader2, CalendarRange,
} from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import OutreachReport from "../components/reports/OutreachReport";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STAGES, STAGE_STYLES, fmtCurrency } from "../lib/constants";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function computeRange(preset) {
  const now = new Date();
  const end = iso(now);
  if (preset === "all") return { start: null, end: null };
  if (preset === "week") {
    const s = new Date(now); s.setDate(now.getDate() - 6);
    return { start: iso(s), end };
  }
  if (preset === "month") return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end };
  if (preset === "quarter") {
    const q = Math.floor(now.getMonth() / 3) * 3;
    return { start: iso(new Date(now.getFullYear(), q, 1)), end };
  }
  if (preset === "year") return { start: iso(new Date(now.getFullYear(), 0, 1)), end };
  return { start: null, end: null };
}

const PRESETS = [
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "quarter", label: "This quarter" },
  { key: "year", label: "This year" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" },
];

function Leaderboard({ rows, currentName }) {
  if (!rows || rows.length === 0) return null;
  const maxWon = Math.max(1, ...rows.map((r) => r.won_value));
  const medals = ["#F59E0B", "#94A3B8", "#B45309"];
  return (
    <div data-testid="report-leaderboard" className="rounded-xl border border-slate-800 bg-[#131C31] p-5 mb-6">
      <h3 className="font-display font-bold text-base mb-4 inline-flex items-center gap-2">
        <Trophy className="w-5 h-5 text-amber-400" /> Salesperson Leaderboard
      </h3>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div
            key={r.salesperson}
            data-testid={`leaderboard-row-${i}`}
            className={`rounded-lg border p-3 ${r.salesperson === currentName ? "border-amber-500/40 bg-amber-500/5" : "border-slate-800 bg-[#0B1120]"}`}
          >
            <div className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                style={{ background: `${medals[i] || "#334155"}22`, color: medals[i] || "#94A3B8", border: `1px solid ${medals[i] || "#334155"}55` }}>
                {i + 1}
              </span>
              <span className="font-semibold text-white flex-1 truncate">{r.salesperson}</span>
              <span className="text-sm font-mono text-emerald-400">{fmtCurrency(r.won_value)}</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500/70" style={{ width: `${(r.won_value / maxWon) * 100}%` }} />
            </div>
            <div className="flex gap-4 mt-2 text-[11px] text-slate-400">
              <span className="inline-flex items-center gap-1"><Trophy className="w-3 h-3" /> {r.won_count} won</span>
              <span className="inline-flex items-center gap-1"><PhoneCall className="w-3 h-3" /> {r.calls_logged} calls</span>
              <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {r.total_leads} leads</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 bg-[#131C31] p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1a`, border: `1px solid ${accent}33` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-400 truncate">{label}</p>
          <p className="font-display font-bold text-lg text-white truncate">{value}</p>
        </div>
      </div>
    </div>
  );
}

function BreakdownList({ title, data, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 bg-[#131C31] p-5">
      <h3 className="font-display font-bold text-base mb-4">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-500 py-4 text-center">No data in this period.</p>
      ) : (
        <div className="space-y-2.5">
          {entries.map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-300 truncate pr-2">{k}</span>
                <span className="text-slate-400 font-mono">{v}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full bg-amber-500/70" style={{ width: `${(v / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimeSeries({ title, series, color, testid }) {
  const max = Math.max(1, ...(series || []).map((p) => p.count));
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 bg-[#131C31] p-5">
      <h3 className="font-display font-bold text-base mb-4">{title}</h3>
      {(!series || series.length === 0) ? (
        <p className="text-sm text-slate-500 py-4 text-center">No activity in this period.</p>
      ) : (
        <div className="flex items-end gap-1 h-32 overflow-x-auto">
          {series.map((p) => (
            <div key={p.date} className="flex flex-col items-center gap-1 shrink-0" style={{ minWidth: 22 }} title={`${p.date}: ${p.count}`}>
              <div className="w-4 rounded-t" style={{ height: `${(p.count / max) * 100}%`, background: color, minHeight: 3 }} />
              <span className="text-[9px] text-slate-500 rotate-0">{p.date.slice(5)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const [mode, setMode] = useState("team"); // "team" | "mine"
  const [salesperson, setSalesperson] = useState("all");
  const [salespeople, setSalespeople] = useState([]);
  const [preset, setPreset] = useState("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outreach, setOutreach] = useState(null);

  useEffect(() => {
    api.get("/salespeople").then(({ data }) => setSalespeople(data)).catch(() => {});
  }, []);

  const effectiveSalesperson = mode === "mine" ? (user?.salesperson || "") : salesperson;

  const range = useMemo(() => {
    if (preset === "custom") return { start: customStart || null, end: customEnd || null };
    return computeRange(preset);
  }, [preset, customStart, customEnd]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (effectiveSalesperson && effectiveSalesperson !== "all") params.salesperson = effectiveSalesperson;
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const { data } = await api.get("/reports", { params });
      setData(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [effectiveSalesperson, range]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary;

  const downloadCsv = () => {
    if (!data) return;
    const rows = [["Metric", "Value"]];
    const label = mode === "mine" ? (user?.salesperson || "Me") : (salesperson === "all" ? "All salespeople" : salesperson);
    rows.push(["Salesperson", label]);
    rows.push(["Period", `${range.start || "beginning"} to ${range.end || "today"}`]);
    rows.push(["Total leads", s.total_leads]);
    rows.push(["Won", s.won_count]);
    rows.push(["Lost", s.lost_count]);
    rows.push(["Win rate (%)", s.win_rate]);
    rows.push(["Won value", s.won_value]);
    rows.push(["Open pipeline", s.open_value]);
    rows.push(["Total value", s.total_value]);
    rows.push(["Calls logged", s.calls_logged]);
    rows.push(["Overdue follow-ups", s.overdue_followups]);
    rows.push(["Upcoming follow-ups (7d)", s.upcoming_followups]);
    rows.push([]);
    rows.push(["Stage", "Count", "Value"]);
    STAGES.forEach((st) => rows.push([st, data.by_stage[st]?.count || 0, data.by_stage[st]?.value || 0]));
    rows.push([]);
    rows.push(["Lead Source", "Count"]);
    Object.entries(data.by_source).forEach(([k, v]) => rows.push([k, v]));
    rows.push([]);
    rows.push(["Industry", "Count"]);
    Object.entries(data.by_industry).forEach(([k, v]) => rows.push([k, v]));
    if (outreach) {
      rows.push([]);
      rows.push(["Outreach (approved by)", "Sent", "Contacted", "Replies", "Reply %", "Positive", "Positive %", "Unsubscribes"]);
      [...outreach.rows, outreach.totals].forEach((r) => rows.push([r.approver, r.emails_sent, r.leads_contacted, r.replies, r.reply_rate, r.positive_replies, r.positive_rate, r.unsubscribes]));
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-${label.replace(/\s+/g, "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  const stageMax = data ? Math.max(1, ...STAGES.map((st) => data.by_stage[st]?.count || 0)) : 1;

  return (
    <AppLayout>
      <div data-testid="reports-page" className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight inline-flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-amber-400" /> Reports
            </h1>
            <p className="text-slate-400 text-sm mt-1">Track sales performance across your pipeline.</p>
          </div>
          <button data-testid="download-report-button" onClick={downloadCsv} disabled={!data} className="bg-slate-800 text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2 text-sm border border-slate-700 hover:bg-slate-700 transition-colors disabled:opacity-50">
            <Download className="w-4 h-4" /> Download CSV
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-slate-800 bg-[#131C31] p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-slate-700 overflow-hidden">
            <button data-testid="reports-mode-mine" onClick={() => setMode("mine")} className={`px-4 py-2 text-sm font-medium ${mode === "mine" ? "bg-amber-500/15 text-amber-400" : "bg-[#0B1120] text-slate-400 hover:text-white"}`}>My Reports</button>
            <button data-testid="reports-mode-team" onClick={() => setMode("team")} className={`px-4 py-2 text-sm font-medium ${mode === "team" ? "bg-amber-500/15 text-amber-400" : "bg-[#0B1120] text-slate-400 hover:text-white"}`}>Team</button>
          </div>

          {mode === "team" && (
            <select data-testid="reports-salesperson-select" value={salesperson} onChange={(e) => setSalesperson(e.target.value)} className="bg-[#0B1120] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500">
              <option value="all">All salespeople</option>
              {salespeople.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          )}

          <div className="inline-flex items-center gap-2 text-slate-400 text-sm">
            <CalendarRange className="w-4 h-4" />
          </div>
          <select data-testid="reports-range-select" value={preset} onChange={(e) => setPreset(e.target.value)} className="bg-[#0B1120] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500">
            {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          {preset === "custom" && (
            <>
              <input data-testid="reports-custom-start" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="bg-[#0B1120] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" />
              <span className="text-slate-500 text-sm">to</span>
              <input data-testid="reports-custom-end" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="bg-[#0B1120] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" />
            </>
          )}
        </div>

        {mode === "mine" && !user?.salesperson && (
          <div data-testid="reports-no-salesperson-notice" className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 mb-6 text-sm text-amber-300">
            You haven't linked your login to a salesperson yet. Set it in <Link to="/app/settings" className="underline font-semibold">Settings</Link> to see your personal numbers. Showing all leads meanwhile.
          </div>
        )}

        {loading || !data ? (
          <div className="flex items-center justify-center py-24 text-slate-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading report…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
              <MetricCard testid="metric-total-leads" icon={Users} label="Leads (period)" value={s.total_leads} accent="#60A5FA" />
              <MetricCard testid="metric-win-rate" icon={Percent} label="Win Rate" value={`${s.win_rate}%`} accent="#F59E0B" />
              <MetricCard testid="metric-won-value" icon={Trophy} label="Won Value" value={fmtCurrency(s.won_value)} accent="#34D399" />
              <MetricCard testid="metric-open-value" icon={TrendingUp} label="Open Pipeline" value={fmtCurrency(s.open_value)} accent="#A78BFA" />
              <MetricCard testid="metric-calls" icon={PhoneCall} label="Calls Logged" value={s.calls_logged} accent="#38BDF8" />
              <MetricCard testid="metric-overdue" icon={AlertTriangle} label="Overdue Follow-ups" value={s.overdue_followups} accent="#F87171" />
            </div>

            <Leaderboard rows={data.leaderboard} currentName={mode === "mine" ? (user?.salesperson || "") : (salesperson !== "all" ? salesperson : "")} />

            <OutreachReport range={range} onData={setOutreach} />

            {/* Pipeline by stage */}
            <div data-testid="report-pipeline-by-stage" className="rounded-xl border border-slate-800 bg-[#131C31] p-5 mb-6">
              <h3 className="font-display font-bold text-base mb-4">Pipeline by Stage</h3>
              <div className="space-y-3">
                {STAGES.map((st) => {
                  const cell = data.by_stage[st] || { count: 0, value: 0 };
                  const style = STAGE_STYLES[st];
                  return (
                    <div key={st} className="flex items-center gap-3">
                      <span className="text-xs w-28 shrink-0" style={{ color: style.text }}>{st}</span>
                      <div className="flex-1 h-4 rounded-full bg-slate-800 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(cell.count / stageMax) * 100}%`, background: style.border, minWidth: cell.count ? 6 : 0 }} />
                      </div>
                      <span className="text-xs text-slate-400 w-8 text-right font-mono">{cell.count}</span>
                      <span className="text-xs text-emerald-400 w-24 text-right font-mono">{fmtCurrency(cell.value)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <TimeSeries testid="report-new-leads-series" title="New Leads Over Time" series={data.new_leads_by_day} color="#60A5FA" />
              <TimeSeries testid="report-activity-series" title="Calls Logged Over Time" series={data.activity_by_day} color="#F59E0B" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <BreakdownList testid="report-by-source" title="Leads by Source" data={data.by_source} />
              <BreakdownList testid="report-by-industry" title="Leads by Industry" data={data.by_industry} />
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
